import { useEffect, useState } from "react";

/**
 * PWA install prompt. Shows:
 *  - "Install App" button on Chrome/Edge/Android when beforeinstallprompt fires
 *  - A one-time iOS hint on Safari (no beforeinstallprompt on iOS)
 * Hides itself if the app is already installed (running in standalone mode) or user dismissed.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Already installed? Hide.
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Previously dismissed? Respect for 30 days.
    const dismissedAt = Number(localStorage.getItem("mh_pwa_dismissed") || 0);
    if (dismissedAt && Date.now() - dismissedAt < 30 * 24 * 60 * 60 * 1000) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS detection (no beforeinstallprompt on iOS Safari)
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOS && isSafari) {
      setIosHint(true);
      setVisible(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("mh_pwa_dismissed", String(Date.now()));
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setVisible(false);
    else dismiss();
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[95%] border border-[#60a5fa]/50 bg-black/95 backdrop-blur px-4 py-3 shadow-[0_0_40px_-10px_rgba(59,130,246,0.6)]"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 shrink-0 border border-[#60a5fa]/50 flex items-center justify-center rounded-md bg-[#05070c]">
          <svg viewBox="0 0 40 40" width="24" height="24" aria-hidden="true">
            <path
              d="M6 32 V8 L20 26 L34 8 V32"
              fill="none"
              stroke="#60a5fa"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs tracking-[0.3em] uppercase text-[#60a5fa] font-mono">
            {iosHint ? "// install money/hub" : "// install app"}
          </div>
          <div className="text-xs text-zinc-300 mt-0.5 leading-tight">
            {iosHint ? (
              <>
                Tap <span className="text-white font-bold">Share</span> then{" "}
                <span className="text-white font-bold">Add to Home Screen</span>
              </>
            ) : (
              <>Install Money/Hub — works offline, one-tap open</>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!iosHint && (
            <button
              data-testid="pwa-install-button"
              onClick={install}
              className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#60a5fa] hover:bg-[#60a5fa] px-3 py-2 font-bold transition-colors"
            >
              Install
            </button>
          )}
          <button
            data-testid="pwa-dismiss-button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="text-zinc-300 hover:text-white text-lg leading-none px-2"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
